import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import CartDrawer from "@/components/CartDrawer";
import LanguageToggle from "@/components/LanguageToggle";
import Logo from "@/components/Logo";

// Fleet-wide Island Gold ticker · locked content.
// Director K ruling (2026-02-03): "SOVEREIGN", "ANONYMOUS", and standalone
// "LIVE" are internal/fleet lexicon and must NOT appear on any public page.
// This is the full approved public list — do not add or reorder.
const ANNOUNCEMENTS_DEFAULT = [
    "Island Botanicals",
    "Wellness",
    "Skincare",
    "10% Philanthropy · CyberKids Academy",
    "Affiliate Disclosed at Pairing",
    "Handcrafted Soap",
];

const ANNOUNCEMENTS_GHOST = ANNOUNCEMENTS_DEFAULT;

export default function Nav() {
    const { count, setOpen } = useCart();
    const { t } = useLang();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [fleetMode, setFleetMode] = useState("curing");
    const { pathname } = useLocation();

    useEffect(() => setMobileOpen(false), [pathname]);
    useEffect(() => {
        api.get("/store/settings")
            .then(({ data }) => setFleetMode(data.fleet_mode || "curing"))
            .catch(() => setFleetMode("curing"));
    }, []);

    const links = [
        { to: "/", label: "Home", key: "home" },
        { to: "/inventory", label: t("nav.inventory"), key: "inventory" },
        { to: "/story", label: t("nav.our_story"), key: "our_story" },
        { to: "/mission", label: t("nav.our_roots"), key: "our_roots" },
        { to: "/about", label: t("nav.the_craft"), key: "the_craft" },
        { to: "/partners", label: t("nav.partners"), key: "partners" },
    ];

    const isGhost = fleetMode === "ghost_protocol";
    const announcements = isGhost ? ANNOUNCEMENTS_GHOST : ANNOUNCEMENTS_DEFAULT;
    const tagline = isGhost ? "Island Gold · Hawaiʻi" : "Island Gold Glow · Hawaiʻi";

    return (
        <>
            {/* Announcement marquee */}
            <div
                className="bg-forest text-cream overflow-hidden"
                data-testid="top-announcement"
                data-fleet-mode={fleetMode}
            >
                <div className="whitespace-nowrap flex marquee-track py-2 text-[0.68rem] tracking-[0.32em] uppercase">
                    {Array.from({ length: 2 }).map((_, i) => (
                        <div key={i} className="flex shrink-0">
                            {announcements.map((t, j) => (
                                <span
                                    key={`${i}-${j}`}
                                    className="mx-8 inline-flex items-center gap-3"
                                >
                                    <span className="text-gold">✦</span>
                                    {t}
                                </span>
                            ))}
                        </div>
                    ))}
                </div>
            </div>

            <header
                className="sticky top-0 z-40 backdrop-blur-md border-b border-forest/10"
                style={{ background: "rgba(10,14,23,0.92)" }}
                data-testid="site-header"
            >
                <div className="max-w-7xl mx-auto px-5 md:px-10 h-[72px] flex items-center justify-between">
                    <button
                        className="md:hidden text-forest"
                        onClick={() => setMobileOpen((v) => !v)}
                        aria-label="Menu"
                        data-testid="mobile-menu-toggle"
                    >
                        {mobileOpen ? <X size={22} /> : <Menu size={22} />}
                    </button>

                    <nav className="hidden md:flex items-center gap-10 text-[0.72rem] tracking-[0.25em] uppercase font-medium">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                end={l.to === "/"}
                                to={l.to}
                                className={({ isActive }) =>
                                    `transition-colors duration-300 hover:text-[color:var(--ig-gold)] ${
                                        isActive ? "text-gold" : "text-forest"
                                    }`
                                }
                                data-testid={`nav-link-${l.key}`}
                            >
                                {l.label}
                            </NavLink>
                        ))}
                    </nav>

                    <Link
                        to="/"
                        className="flex flex-col items-center leading-none select-none"
                        data-testid="brand-logo"
                    >
                        <Logo size="md" variant="light" />
                        <span className="text-[0.55rem] tracking-[0.45em] uppercase text-forest/80 font-medium mt-1.5">
                            {tagline}
                        </span>
                    </Link>

                    <div className="flex items-center gap-4">
                        <LanguageToggle className="hidden md:inline-flex text-forest" />
                        <Link
                            to="/inventory"
                            className="hidden sm:inline text-[0.72rem] tracking-[0.25em] uppercase text-forest hover:text-gold transition-colors duration-300"
                            data-testid="nav-shop-quick"
                        >
                            {t("nav.cart")}
                        </Link>
                        <button
                            type="button"
                            onClick={() => setOpen(true)}
                            className="relative text-forest hover:text-gold transition-colors duration-300"
                            aria-label="Open cart"
                            data-testid="nav-cart-button"
                        >
                            <ShoppingBag size={22} />
                            {count > 0 && (
                                <span
                                    className="absolute -top-2 -right-2 bg-gold text-forest rounded-full text-[0.62rem] font-semibold w-5 h-5 flex items-center justify-center"
                                    data-testid="cart-count-badge"
                                >
                                    {count}
                                </span>
                            )}
                        </button>
                    </div>
                </div>

                {/* Mobile menu */}
                {mobileOpen && (
                    <div
                        className="md:hidden border-t border-forest/10 bg-cream"
                        data-testid="mobile-menu"
                    >
                        <div className="px-6 py-6 flex flex-col gap-4 text-sm uppercase tracking-[0.25em]">
                            {links.map((l) => (
                                <NavLink
                                    key={l.to}
                                    end={l.to === "/"}
                                    to={l.to}
                                    className="text-forest hover:text-gold"
                                    data-testid={`mobile-nav-${l.key}`}
                                >
                                    {l.label}
                                </NavLink>
                            ))}
                            <Link
                                to="/partner/auth"
                                className="text-forest hover:text-gold border-t border-forest/10 pt-4"
                                data-testid="mobile-nav-partner-signin"
                            >
                                Partner sign in
                            </Link>
                        </div>
                    </div>
                )}
            </header>
            <CartDrawer />
        </>
    );
}
